/**
 * INCREMENTAL /api/stocks/minichart POLLS (register minichart-polls-refetch-1400-bars-1101).
 *
 * Every consumer polled the full 1,400 one-minute bars for each symbol every 10-20s: 143KB of JSON
 * (28KB brotli) per symbol per poll, almost all of it bars the client already held. A poll now asks
 * only for bars from `since` = its newest bar minus an OVERLAP, and replaces its tail from there.
 *
 * ⚠ WHY THE OVERLAP. A bar can be written after a newer one (late ingest) or revised in place. Measured
 *   on prod 10-29/10-30 over the live, live_fast_lane and late_backlog sources: max lag created_at -
 *   timestamp 2m52s, p99.9 2m15s. The 10-minute overlap re-reads that window on every poll, 3.5x the
 *   worst lag seen. rest_backfill rows land only after the session (0 of 79,232 inside it), and a FULL
 *   refetch every FULL_EVERY_MS picks up anything older that changed anyway.
 * ⚠ WHY THE ECHO. A server that ignores `since` (an old build during a deploy swap) returns the whole
 *   window; merging that onto the kept head would duplicate it. Merge only when the body echoes the
 *   exact `since` that was asked for; otherwise treat the body as a full replacement.
 */
export const MINICHART_MAX_BARS = 1400
export const MINICHART_OVERLAP_MS = 10 * 60_000
export const MINICHART_FULL_EVERY_MS = 10 * 60_000

export type MinichartState<T extends { t: number }> = { bars: T[]; fullAt: number }

/** The `since` to ask for, or null for a full fetch (nothing held yet, or the last full one is old). */
export function minichartSince<T extends { t: number }>(state: MinichartState<T> | undefined, now: number): number | null {
  if (!state || !state.bars.length || now - state.fullAt >= MINICHART_FULL_EVERY_MS) return null
  return state.bars[state.bars.length - 1].t - MINICHART_OVERLAP_MS
}

export function minichartUrl(symbol: string, since: number | null): string {
  const base = `/api/stocks/minichart?symbol=${encodeURIComponent(symbol)}`
  return since == null ? base : `${base}&since=${since}`
}

/** Apply one response. Returns the next state, or the previous one when the body carries no bars. */
export function applyMinichart<T extends { t: number }>(
  prev: MinichartState<T> | undefined,
  body: { bars?: unknown; since?: unknown } | null | undefined,
  askedSince: number | null,
  now: number,
): MinichartState<T> | undefined {
  const fresh = Array.isArray(body?.bars) ? (body!.bars as T[]) : null
  if (!fresh) return prev
  const incremental = askedSince != null && body?.since === askedSince && !!prev
  if (!incremental) return fresh.length ? { bars: fresh, fullAt: now } : prev
  return { bars: mergeBarTail(prev!.bars, fresh, askedSince!, (b) => b.t, MINICHART_MAX_BARS), fullAt: prev!.fullAt }
}

/** The held bars before `since`, then every fresh bar (which covers since onward), trimmed to maxBars.
 *  Shared with the /chart candles poll (register chart-candles-polls-refetch-full-window-1101), whose bars
 *  key on `timestamp`, hence the accessor. */
/** /chart: the full window last applied, keyed by the request it answers (symbol|days|tf). */
export type HeldWindow<T> = { key: string; bars: T[]; fullAt: number }

/** /chart: the `since` to send, or null for a full fetch (nothing held for this key, or the full one is old). */
export function heldSince<T>(held: HeldWindow<T> | null, key: string, now: number, timeOf: (b: T) => number): number | null {
  if (!held || held.key !== key || !held.bars.length || now - held.fullAt >= MINICHART_FULL_EVERY_MS) return null
  return timeOf(held.bars[held.bars.length - 1]) - MINICHART_OVERLAP_MS
}

/** /chart: the window after one response. Merges only on an exact echo; otherwise a non-empty body replaces. */
export function applyHeld<T>(
  held: HeldWindow<T> | null, key: string, fresh: T[] | null, echoed: unknown, since: number | null, now: number,
  timeOf: (b: T) => number,
): HeldWindow<T> | null {
  if (!fresh) return held
  if (since != null && held && held.key === key && echoed === since) {
    return { key, bars: mergeBarTail(held.bars, fresh, since, timeOf), fullAt: held.fullAt }
  }
  return fresh.length ? { key, bars: fresh, fullAt: now } : held
}

/** Server side: a valid `since` query value in ms, or null (absent or junk, so the caller serves the full body). */
export function parseSince(raw: string | null): number | null {
  return raw != null && /^\d{1,15}$/.test(raw) ? Number(raw) : null
}

/** Server side: the bars a `since` request gets. The caller echoes `since` beside them. */
export function sliceSince<T>(bars: T[], since: number, timeOf: (b: T) => number): T[] {
  return bars.filter((b) => timeOf(b) >= since)
}

export function mergeBarTail<T>(held: T[], fresh: T[], since: number, timeOf: (b: T) => number, maxBars = Infinity): T[] {
  const bars = held.filter((b) => timeOf(b) < since).concat(fresh)
  return bars.length > maxBars ? bars.slice(-maxBars) : bars
}
